import React, { useEffect, useMemo, useState } from "react";
import cross_icon from "../Components/Assets/cross_icon.png";
import { backend_url, currency } from "../App";
import { adminPost, getCategories, uploadImage } from "../common/api/api";
import {
  getActiveCategories,
  getCategoryDisplay,
  getMainCategorySlug,
  getSubcategoriesForMain,
} from "../common/categoryTree";

const AdminListProduct = () => {
  const [allproducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editProduct, setEditProduct] = useState(null); // product being edited
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchInfo = () => {
    fetch(`${backend_url}/allproducts`)
      .then((res) => res.json())
      .then((data) => setAllProducts(data));
  };

  useEffect(() => {
    fetchInfo();
    getCategories().then((data) => setCategories(getActiveCategories(data))).catch(() => setCategories([]));
  }, []);

  const availableSubcategories = useMemo(
    () => getSubcategoriesForMain(categories, editForm.mainCategory),
    [categories, editForm.mainCategory]
  );

  const removeProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await adminPost("/removeproduct", { id });
    fetchInfo();
  };

  const openEdit = (product) => {
    const mainCategory = getMainCategorySlug(product, categories);
    const details = getCategoryDisplay(product, categories);
    setEditProduct(product);
    setEditForm({
      id: product.id,
      name: product.name,
      description: product.description || "",
      category: product.category,
      mainCategory,
      subCategory: product.subCategory || (details.subName && String(product.category || "").toLowerCase()) || "",
      images: product.images || [],
      old_price: product.old_price,
      new_price: product.new_price,
      image: product.image,
    });
  };

  const saveEdit = async () => {
    setSaving(true);
    const payload = {
      ...editForm,
      images: editForm.image
        ? (editForm.images?.length ? [editForm.image, ...editForm.images.slice(1)] : [editForm.image])
        : (editForm.images || []),
    };
    const data = await adminPost("/updateproduct", payload);
    setSaving(false);
    if (data.success) {
      setEditProduct(null);
      fetchInfo();
    } else {
      alert(data.error || "Failed to update product");
    }
  };

  const handleMainImageUpload = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("product", file);
      const resp = await uploadImage(formData);
      if (!resp?.success || !resp.image_url) {
        alert("Image upload failed");
        return;
      }
      setEditForm((prev) => ({
        ...prev,
        image: resp.image_url,
        images: prev.images?.length ? [resp.image_url, ...prev.images.slice(1)] : [resp.image_url],
      }));
    } catch (err) {
      alert(err.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const inputClass =
    "w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 transition-all text-sm";

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-white mb-6">All Products</h1>

      {/* Scrollable table wrapper */}
      <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
        <div className="min-w-[640px]">
      {/* Header Row */}
      <div className="grid grid-cols-[80px_1fr_100px_100px_120px_100px] items-center gap-4 px-4 py-3 bg-white/[0.05] rounded-t-xl text-[#a0aec0] text-sm font-semibold">
        <span>Image</span>
        <span>Title</span>
        <span>Old Price</span>
        <span>New Price</span>
        <span>Category</span>
        <span>Actions</span>
      </div>

      {/* Product Rows */}
      <div className="rounded-b-xl overflow-hidden">
        {allproducts.length === 0 ? (
          <div className="py-12 text-center text-[#4a5568]">No products found.</div>
        ) : (
          allproducts.map((product, index) => (
            <div
              key={index}
              className="grid grid-cols-[80px_1fr_100px_100px_120px_100px] items-center gap-4 px-4 py-3 border-b border-white/[0.05] hover:bg-white/[0.03] transition-colors last:border-b-0"
            >
              <img
                src={
                  String(product.image).startsWith("http")
                    ? product.image
                    : backend_url + product.image
                }
                alt={product.name}
                className="w-14 h-14 object-cover rounded-lg bg-white/[0.05]"
              />
              <span className="text-[#e2e8f0] text-sm font-medium truncate pr-2">
                {product.name}
              </span>
              <span className="text-[#a0aec0] text-sm">
                {currency}{product.old_price}
              </span>
              <span className="text-[#00d4ff] text-sm font-semibold">
                {currency}{product.new_price}
              </span>
              <span className="text-[#a0aec0] text-sm capitalize">
                {getCategoryDisplay(product, categories).label}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(product)}
                  title="Edit"
                  className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-[rgba(0,212,255,0.15)] transition-colors cursor-pointer text-[#00d4ff] text-sm"
                >
                  ✏️
                </button>
                <button
                  onClick={() => removeProduct(product.id)}
                  title="Delete"
                  className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-red-500/20 transition-colors cursor-pointer"
                >
                  <img src={cross_icon} alt="Remove" className="w-4 h-4 opacity-70 hover:opacity-100" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
        </div>
      </div>

      <p className="mt-3 text-sm text-[#4a5568]">{allproducts.length} products total</p>

      {/* Edit Modal */}
      {editProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
          <div className="bg-[#0d1527] border border-white/[0.1] rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-white">Edit Product</h2>
              <button onClick={() => setEditProduct(null)} className="text-[#4a5568] hover:text-white text-2xl leading-none cursor-pointer">×</button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#a0aec0] mb-1 block">Product Name</label>
                <input className={inputClass} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder="Product name" />
              </div>
              <div>
                <label className="text-xs text-[#a0aec0] mb-1 block">Description</label>
                <input className={inputClass} value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" />
              </div>
              <div>
                <label className="text-xs text-[#a0aec0] mb-1 block">Main Image URL</label>
                <input className={inputClass} value={editForm.image || ""} onChange={e => setEditForm({ ...editForm, image: e.target.value })} placeholder="https://example.com/product.png" />
              </div>
              <div className="rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] p-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm text-[#e2e8f0] font-medium">Or upload replacement image</p>
                    <p className="text-[11px] text-[#4a5568] mt-1">Supports standard and AI-generated JPG, PNG, WebP, or AVIF files.</p>
                  </div>
                  <label className="px-3 py-2 rounded-lg border border-[rgba(0,212,255,0.3)] text-[#00d4ff] text-sm cursor-pointer hover:bg-[rgba(0,212,255,0.08)] transition-colors">
                    {uploading ? "Uploading..." : "Choose File"}
                    <input
                      type="file"
                      accept="image/*,.avif"
                      hidden
                      disabled={uploading}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) handleMainImageUpload(file);
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#a0aec0] mb-1 block">Original Price</label>
                  <input className={inputClass} type="number" value={editForm.old_price} onChange={e => setEditForm({ ...editForm, old_price: e.target.value })} placeholder="0" />
                </div>
                <div>
                  <label className="text-xs text-[#a0aec0] mb-1 block">Sale Price</label>
                  <input className={inputClass} type="number" value={editForm.new_price} onChange={e => setEditForm({ ...editForm, new_price: e.target.value })} placeholder="0" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#a0aec0] mb-1 block">Main Category</label>
                  <select className={inputClass} value={editForm.mainCategory || ""} onChange={e => setEditForm({ ...editForm, mainCategory: e.target.value, subCategory: "", category: e.target.value })}>
                    {categories.map((cat) => (
                      <option key={cat._id} value={cat.slug}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#a0aec0] mb-1 block">Subcategory</label>
                  <select className={inputClass} value={editForm.subCategory || ""} onChange={e => setEditForm({ ...editForm, subCategory: e.target.value, category: e.target.value || editForm.mainCategory })}>
                    <option value="">No subcategory</option>
                    {availableSubcategories.map((sub) => (
                      <option key={sub._id} value={sub.slug}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button
                onClick={saveEdit}
                disabled={saving}
                className="flex-1 py-2.5 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg text-sm hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={() => setEditProduct(null)}
                className="px-5 py-2.5 border border-white/[0.1] text-[#a0aec0] rounded-lg text-sm hover:border-white/[0.25] cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminListProduct;
